import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import './Reports.css';

interface GoalEventRow {
  id: string;
  date: string;
  time: string;
  dateTime: string;
  clickTimeDisplay: string;
  goalName: string;
  offerId: number;
  offer: string;
  affiliateName: string;
  subAffiliate: string;
  advertiser: string;
  clickId: string;
  affiliateClickId: string;
  ip: string;
  blockReason: string;
  blockSubReason: string;
}

const GoalReport: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState<string>('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [goals, setGoals] = useState<GoalEventRow[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGoalReport = async () => {
      try {
        setLoading(true);
        const response = await apiService.getGoalReport({
          period: periodFilter,
          search: searchTerm
        });
        setGoals(Array.isArray(response.data) ? response.data : []);
      } catch (error: any) {
        console.error('Failed to fetch goal report:', error);
        setGoals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGoalReport();
  }, [periodFilter, searchTerm]);

  const filteredGoals = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return goals;

    return goals.filter((goal) => (
      goal.goalName.toLowerCase().includes(search) ||
      goal.offer.toLowerCase().includes(search) ||
      goal.affiliateName.toLowerCase().includes(search) ||
      goal.subAffiliate.toLowerCase().includes(search) ||
      goal.advertiser.toLowerCase().includes(search) ||
      goal.clickId.toLowerCase().includes(search)
    ));
  }, [goals, searchTerm]);

  const exportCsv = () => {
    const headers = [
      'Date',
      'Time',
      'Click Time',
      'Goal Name',
      'Offer ID',
      'Offer',
      'Affiliate Name',
      'Sub Affiliate',
      'Advertiser',
      'Click ID',
      'Affiliate Click ID',
      'IP',
      'Block Reason',
      'Block Sub Reason'
    ];

    const rows = filteredGoals.map((goal) => [
      goal.date,
      goal.time,
      goal.clickTimeDisplay,
      goal.goalName,
      goal.offerId,
      goal.offer,
      goal.affiliateName,
      goal.subAffiliate || 'N/A',
      goal.advertiser,
      goal.clickId,
      goal.affiliateClickId || 'N/A',
      goal.ip,
      goal.blockReason,
      goal.blockSubReason
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `goal-report-${periodFilter}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Goal Report</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Goal Events</h3>
          <div className="value">{filteredGoals.length}</div>
        </div>
        <div className="summary-card">
          <h3>Unique Goals</h3>
          <div className="value">{new Set(filteredGoals.map((goal) => goal.goalName)).size}</div>
        </div>
        <div className="summary-card">
          <h3>Unique Offers</h3>
          <div className="value">{new Set(filteredGoals.map((goal) => goal.offerId)).size}</div>
        </div>
        <div className="summary-card">
          <h3>Blocked/Rejected</h3>
          <div className="value rejected">
            {filteredGoals.filter((goal) => goal.blockReason && goal.blockReason !== 'N/A').length}
          </div>
        </div>
      </div>

      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Period:</label>
            <select
              value={periodFilter}
              onChange={(event) => setPeriodFilter(event.target.value)}
              className="period-select"
            >
              <option value="current">Current Month</option>
              <option value="previous">Previous Month</option>
              <option value="all">Last 90 Days</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search goal, offer, affiliate, advertiser, click..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="export-buttons">
          <button className="btn btn-success" onClick={exportCsv}>
            CSV
          </button>
        </div>
      </div>

      <div className="report-content">
        <div className="table-container">
          <table className="report-table goal-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Click Time</th>
                <th>Goal Name</th>
                <th>Offer ID</th>
                <th>Offer</th>
                <th>Affiliate Name</th>
                <th>Sub Affiliate</th>
                <th>Advertiser</th>
                <th>Click ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="loading-cell">Loading goal report...</td>
                </tr>
              ) : filteredGoals.length === 0 ? (
                <tr>
                  <td colSpan={9} className="loading-cell">No goal events found for this period.</td>
                </tr>
              ) : (
                filteredGoals.map((goal) => (
                  <React.Fragment key={goal.id}>
                    <tr
                      className={`goal-report-row ${expandedRow === goal.id ? 'is-expanded' : ''}`}
                      onClick={() => setExpandedRow(expandedRow === goal.id ? null : goal.id)}
                    >
                      <td>
                        <div className="datetime-cell">
                          <span className="date">{goal.date}</span>
                          <span className="time">{goal.time}</span>
                        </div>
                      </td>
                      <td>{goal.clickTimeDisplay}</td>
                      <td><span className="event-type event">{goal.goalName}</span></td>
                      <td>{goal.offerId}</td>
                      <td>{goal.offer}</td>
                      <td>{goal.affiliateName}</td>
                      <td>{goal.subAffiliate || 'N/A'}</td>
                      <td>{goal.advertiser}</td>
                      <td><span className="click-id">{goal.clickId}</span></td>
                    </tr>
                    {expandedRow === goal.id && (
                      <tr className="goal-expanded-row">
                        <td colSpan={9} className="goal-expanded-cell">
                          <div className="goal-details-grid">
                            <div className="goal-detail-item">
                              <span className="goal-detail-label">Affiliate Click ID</span>
                              <span className="goal-detail-value">{goal.affiliateClickId || 'N/A'}</span>
                            </div>
                            <div className="goal-detail-item">
                              <span className="goal-detail-label">IP</span>
                              <span className="goal-detail-value">{goal.ip}</span>
                            </div>
                            <div className="goal-detail-item">
                              <span className="goal-detail-label">Block Reason</span>
                              <span className="goal-detail-value">{goal.blockReason}</span>
                            </div>
                            <div className="goal-detail-item">
                              <span className="goal-detail-label">Block Sub Reason</span>
                              <span className="goal-detail-value">{goal.blockSubReason}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredGoals.length} goal events for {periodFilter === 'all' ? 'last 90 days' : `${periodFilter} month`}
        </div>
      </div>
    </div>
  );
};

export default GoalReport;
